/* eslint-disable @next/next/no-img-element */
import "react-responsive-carousel/lib/styles/carousel.min.css";
import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import { Carousel } from "react-responsive-carousel";
import { useRouter } from "next/router";
import { cn } from "@/lib/utils";
import { STATIC_PAGES } from "@components/layout/header";
import { Property } from "csstype";

export type HeroCarouselItem = {
  desktopUrl: string;
  mobileUrl?: string;
  href?: string;
  alt: string;
  hidePaths?: string[];
  featuredPaths?: string[];
};

interface HeroCarouselProps {
  initialIndex: number;
  items: HeroCarouselItem[];
}

const HeroCarousel: React.FC<HeroCarouselProps> = (props) => {
  const router = useRouter();
  const resolvedPath = router.asPath.replace(/\?[^#]*/, "").toLowerCase();
  const normalizedItems = useMemo(() => {
    const visible = props.items.filter(
      (item) => !item.hidePaths?.includes(resolvedPath)
    );
    return [...visible].sort((a, b) => {
      const aFeatured = a.featuredPaths?.includes(resolvedPath) ? 0 : 1;
      const bFeatured = b.featuredPaths?.includes(resolvedPath) ? 0 : 1;
      return aFeatured - bFeatured;
    });
  }, [props.items, resolvedPath]);
  const [selectedItem, setSelectedItem] = useState(props.initialIndex);

  useEffect(() => {
    setSelectedItem(0);
  }, [resolvedPath]);

  const RenderImage: React.FC<{
    heroCarouselItem: HeroCarouselItem;
    pointerEvents: Property.PointerEvents;
  }> = (props) => {
    const { desktopUrl, mobileUrl, href, alt } = props.heroCarouselItem;
    const imageClassName = "mx-auto w-full max-w-7xl";
    const mobileImageClassName = "block w-full md:hidden";

    return (
      <>
        <div className="mx-auto hidden max-w-7xl cursor-pointer text-center md:block lg:px-6">
          {href ? (
            <Link href={href} passHref>
              {/* The id "ad-banner" because it will be used by marketing/data for tracking in GA */}
              <a id="ad-banner">
                <img
                  style={{ pointerEvents: props.pointerEvents }}
                  className={imageClassName}
                  alt={alt}
                  src={desktopUrl}
                />
              </a>
            </Link>
          ) : (
            <img
              style={{ pointerEvents: props.pointerEvents }}
              className={imageClassName}
              alt={alt}
              src={desktopUrl}
            />
          )}
        </div>
        <div className="block w-full cursor-pointer md:hidden">
          {href ? (
            <Link href={href} passHref>
              <a>
                <img
                  style={{ pointerEvents: props.pointerEvents }}
                  className={mobileImageClassName}
                  alt={alt}
                  src={mobileUrl || desktopUrl}
                />
              </a>
            </Link>
          ) : (
            <img
              style={{ pointerEvents: props.pointerEvents }}
              className={mobileImageClassName}
              alt={alt}
              src={mobileUrl || desktopUrl}
            />
          )}
        </div>
      </>
    );
  };

  if (
    normalizedItems &&
    typeof window !== "undefined" &&
    window?.location?.host != "about.luminews.my" &&
    !STATIC_PAGES.includes(router.pathname)
  ) {
    return (
      <div className="relative mx-auto max-w-7xl cursor-pointer text-center lg:px-6">
        <Carousel
          autoPlay={true}
          infiniteLoop
          selectedItem={selectedItem}
          axis="horizontal"
          onChange={(e) => setSelectedItem(e)}
          stopOnHover={true}
          transitionTime={1000}
          interval={4000}
          showStatus={false}
          showThumbs={false}
          showArrows={false}
        >
          {normalizedItems.map((item, index) => {
            return (
              <div key={index}>
                {item.href ? (
                  <RenderImage heroCarouselItem={item} pointerEvents={"auto"} />
                ) : (
                  <RenderImage heroCarouselItem={item} pointerEvents={"none"} />
                )}
              </div>
            );
          })}
        </Carousel>
        {normalizedItems && normalizedItems.length > 1 && (
          <div className="absolute inset-x-0 bottom-[8px] z-10 flex justify-center gap-x-2">
            {normalizedItems.map((item, index) => {
              return (
                <div
                  key={index}
                  onClick={() => setSelectedItem(index)}
                  className={cn(
                    "transitions-color h-2 w-2 rounded-full shadow-custom duration-300",
                    selectedItem === index ? "bg-primary" : "bg-disabled"
                  )}
                ></div>
              );
            })}
          </div>
        )}
      </div>
    );
  } else {
    return null;
  }
};

export default HeroCarousel;
